import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { syncBilling } from '../services/stripeBilling';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const CACHE_KEY = 'repbuddy_access_status';

// Last known access decision, persisted so returning users skip the "Checking your
// access…" spinner while we silently revalidate (stale-while-revalidate). Cleared on
// sign-out (see AuthContext) so a different user can't inherit it.
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeCache(decision) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(decision)); } catch { /* quota / disabled */ }
}

// Asks the backend whether the current visitor may enter the chat app.
// Returns { loading, error, canUseChat, isDeveloper, chatEnabled, launchAt, refetch }.
// Developers (in allowed_users) get canUseChat regardless of the chat_enabled flag;
// everyone else only gets it once an admin flips the launch switch on.
export function useAccessStatus() {
  const { getToken, session } = useAuth();
  // Seed from cache so the first paint is the real screen, not a spinner.
  const seed = useRef(readCache()).current;
  const [state, setState] = useState(seed
    ? { loading: false, error: false, canUseChat: !!seed.canUseChat, isDeveloper: !!seed.isDeveloper, chatEnabled: !!seed.chatEnabled, mustSubscribe: !!seed.mustSubscribe, subscribeReason: seed.subscribeReason || null, trialEndsAt: seed.trialEndsAt || null, launchAt: seed.launchAt || null }
    : { loading: true, error: false, canUseChat: false, isDeveloper: false, chatEnabled: false, mustSubscribe: false, subscribeReason: null, trialEndsAt: null, launchAt: null }
  );
  const [nonce, setNonce] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  /**
   * Ask Stripe, then re-read access.
   *
   * `refetch` alone only re-reads OUR database, so if the webhook never arrived it
   * cheerfully returns the same "no subscription" answer forever - which is exactly what a
   * paying customer pressing "Refresh access" experiences. This syncs from Stripe first, so
   * the button can actually recover the account rather than just re-asking a stale question.
   */
  const recheck = useCallback(async () => {
    setSyncing(true);
    try {
      await syncBilling({ token: getToken?.() });
    } finally {
      setSyncing(false);
      setNonce((n) => n + 1);
    }
    // getToken is intentionally not a dependency; it is not referentially stable and the
    // token is re-read on every call anyway. Mirrors the fetch effect below.
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only block on the spinner when there's nothing cached to show meanwhile.
      setState((s) => ({ ...s, loading: !readCache(), error: false }));
      try {
        const token = getToken();
        const res = await fetch(`${BACKEND_URL}/api/access-status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`access-status ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const decision = {
          canUseChat: !!data.canUseChat,
          isDeveloper: !!data.isDeveloper,
          chatEnabled: !!data.chatEnabled,
          mustSubscribe: !!data.mustSubscribe,
          subscribeReason: data.subscribeReason || null,
          trialEndsAt: data.trialEndsAt || null,
          launchAt: data.launchAt || null,
        };
        writeCache(decision);
        setState({ loading: false, error: false, ...decision });
      } catch {
        if (cancelled) return;
        // Fail closed only when we have no prior decision to fall back on; otherwise
        // keep showing the cached screen so a transient blip doesn't blank the app.
        setState((s) => (readCache()
          ? { ...s, loading: false, error: false }
          : { ...s, loading: false, error: true, canUseChat: false }));
      }
    })();
    return () => { cancelled = true; };
    // getToken is intentionally omitted - it isn't referentially stable and the token is
    // re-read on every run anyway; session/nonce are the real triggers.
  }, [session, nonce]);

  // Re-check access when the user returns to the tab, so a trial that expires or leads that
  // run out mid-session surface the paywall shortly after, without a manual reload.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') refetch(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetch]);

  // Returning from Stripe Checkout: sync before trusting our own answer.
  //
  // This is the moment that strands people. Stripe has taken the money and redirected them
  // back, but the webhook that grants access may be seconds behind, may be retrying, or may
  // never arrive at all. Waiting for it means showing a paywall to someone who just paid.
  // Syncing here makes the return from checkout self-healing regardless of webhook health.
  //
  // Runs once per arrival: the flag is stripped from the URL immediately so a refresh or a
  // back-navigation does not sync again.
  useEffect(() => {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    if (params.get('checkout') !== 'success') return;

    params.delete('checkout');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);

    recheck();
  }, [recheck]);

  return { ...state, syncing, refetch, recheck };
}
